import Link from 'next/link';
import React, { MouseEventHandler } from 'react';

import styled from '@emotion/styled';
import type { Route } from 'next';

import { TileHyperLinkIcon } from '@web/ui/icons';
import { getStateLayerColor } from '@web/ui/theme/utils';

const Container = styled.div<{ variant: 'top' | 'left' }>`
  cursor: pointer;
  width: 100%;
  height: 100%;
  display: flex;
  flex: 1 0 0;
  align-self: stretch;
  flex-direction: ${({ variant }) => (variant === 'top' ? 'column' : 'row')};
  align-items: ${({ variant }) =>
    variant === 'top' ? 'flex-start' : 'center'};
  justify-content: ${({ variant }) =>
    variant === 'top' ? 'center' : 'flex-start'};
  grid-row: 1 / span 1;
  background: ${({ theme }) => theme.color.surfaceContainerLow};
  border-radius: ${({ theme }) => theme.shape.corner['2000']};
  position: relative;

  ${({ theme }) => theme.breakpoints.down('lg')} {
    display: flex;
    flex-direction: column;
    justify-content: center;
    align-items: center;
    align-self: stretch;
  }

  &:hover {
    background-image: ${getStateLayerColor('onSurface', 'hover')};
  }

  :active {
    border-radius: ${({ theme }) => theme.shape.corner['2500']};
    background-image: ${getStateLayerColor('onSurface', 'active')};
  }
`;

const TopRowContainer = styled.div`
  display: flex;
  align-items: center;
  justify-content: space-between;
  position: absolute;
  inset-inline: 0;
  top: 0;
  gap: ${({ theme }) => theme.spacing('1000')};
  padding: ${({ theme }) => theme.spacing('2000')};
  align-self: stretch;
`;

const TileTitle = styled.span`
  font: ${({ theme }) => theme.typography.mono.regular};
  font-size: ${({ theme }) => theme.typescale.size['0875']};
  color: ${({ theme }) => theme.color.onSurface};
`;

const IllustrationWrapper = styled.div<{ variant: 'top' | 'left' }>`
  display: flex;
  align-items: center;
  justify-content: center;
  flex: ${({ variant }) => (variant === 'left' ? '1 1 50%' : '0 0 auto')};
  align-self: ${({ variant }) => (variant === 'top' ? 'stretch' : 'auto')};
  min-width: 0;
  overflow: hidden;

  ${({ theme }) => theme.breakpoints.down('lg')} {
    flex: 0 0 auto;
    width: 100%;
  }
`;

const Content = styled.div<{ variant: 'top' | 'left' }>`
  display: flex;
  flex-direction: column;
  align-items: flex-start;
  gap: ${({ theme }) => theme.spacing('2500')};
  padding: ${({ theme }) => theme.spacing('2000')};
  flex: ${({ variant }) => (variant === 'left' ? '1 1 50%' : '0 0 auto')};
`;

const TextContainer = styled.div`
  display: flex;
  flex-direction: column;
  justify-content: center;
  align-items: flex-start;
  align-self: stretch;
  gap: ${({ theme }) => theme.spacing('0750')};
  flex: 1 0 0;
`;

const SubTitle = styled.h3`
  color: ${({ theme }) => theme.color.onSurface};
  font: ${({ theme }) => theme.typography.body.bold};
  font-size: ${({ theme }) => theme.typescale.size['4']};
  line-height: ${({ theme }) => theme.typescale.lineHeight.extended};
`;

const Description = styled.p`
  color: ${({ theme }) => theme.color.onSurface};
  font: ${({ theme }) => theme.typography.body.regular};
  font-size: ${({ theme }) => theme.typescale.size['1']};
  line-height: ${({ theme }) => theme.typescale.lineHeight.extended};
`;

const LinkText = styled(Link)`
  display: inline-flex;
  align-items: center;
  gap: ${({ theme }) => theme.spacing('0500')};
  color: ${({ theme }) => theme.color.onSurface};
  font: ${({ theme }) => theme.typography.mono.regular};
  font-size: ${({ theme }) => theme.typescale.size['0875']};
  line-height: ${({ theme }) => theme.typescale.lineHeight.extended};
  text-decoration: none;
  position: relative;

  &:hover {
    text-decoration: underline;
  }
`;

const BadgeContainer = styled.div`
  position: absolute;
  right: ${({ theme }) => theme.spacing('2000')};
  top: ${({ theme }) => theme.spacing('2000')};
  color: ${({ theme }) => theme.color.onSurface};
  z-index: 1;
`;

interface IGenericHubTileProps {
  variant: 'top' | 'left';
  title: string;
  subtitle: string;
  illustration?: React.ReactNode;
  description?: string;
  badge?: React.ReactNode;
  className?: string;
  href?: Route;
  linkText?: string;
  onClick?: MouseEventHandler<HTMLDivElement>;
}

export const GenericHubTile = ({
  variant,
  badge,
  illustration,
  title,
  subtitle,
  description,
  className,
  href,
  linkText,
  onClick,
}: IGenericHubTileProps) => {
  return (
    <Container onClick={onClick} className={className} variant={variant}>
      <TopRowContainer>
        <TileTitle>{title}</TileTitle>
        <BadgeContainer>{badge}</BadgeContainer>
      </TopRowContainer>

      {illustration && (
        <IllustrationWrapper variant={variant}>
          {illustration}
        </IllustrationWrapper>
      )}

      <Content variant={variant}>
        <TextContainer>
          <SubTitle>{subtitle}</SubTitle>
          <Description>{description}</Description>
        </TextContainer>

        {href && (
          <LinkText href={href}>
            {linkText}
            <TileHyperLinkIcon size="0750" />
          </LinkText>
        )}
      </Content>
    </Container>
  );
};
